// Drawer «Asignar tiendas» (handoff §6): Seg «Todas las tiendas / Tiendas
// específicas» + StorePicker con el diff contra lo persistido
// (originalIds). Un solo paso, como en el handoff; la lógica de guardado
// es la de ChecklistTemplateAssignDrawer sobre el catálogo de
// mantenciones:
//   - mismo alcance «tiendas», cambia la lista → PUT /:id/assets
//   - marca → tiendas → PATCH { scope: "assets", assetIds } (el backend
//     rechaza el cambio sin tiendas: MAINTENANCE_ITEM_SCOPE_EMPTY)
//   - tiendas → marca → PATCH { scope: "brand" }
// «Todas» es el alcance `brand` del backend: todas las tiendas de la
// organización cuando el ítem no tiene marca, o todas las de su marca.
// Las candidatas vienen filtradas por esa marca, así el
// MAINTENANCE_ITEM_BRAND_MISMATCH no se puede producir desde aquí. Con
// «Todas» elegido no se pinta nada bajo el Seg salvo el hint: el
// prototipo no define otro contenido ahí y el handoff pide no inventar.

import { useEffect, useId, useMemo, useRef, useState } from "react";
import type {
  MaintenanceItemAdminSummary,
  MaintenanceItemUpdateBody,
} from "@bitacorapro/api-types";
import { Drawer } from "../../../components/Drawer";
import { PageError } from "../../../components/PageError";
import { PageSkeleton } from "../../../components/PageSkeleton";
import { Seg } from "../../../components/Seg";
import { StorePicker, type StorePickerItem } from "../../../components/StorePicker";
import { StorePickerSkel } from "../../../components/skeleton-compositions/StorePickerSkel";
import { ApiError } from "../../../lib/api/error";
import { useQueryView } from "../../../lib/loading/useQueryView";
import {
  useMaintenanceItemAssets,
  usePatchMaintenanceItem,
  usePutMaintenanceItemAssets,
} from "./queries";
import type { MantencionWords } from "./words";

type Scope = MaintenanceItemAdminSummary["scope"];

// maintenanceItemUpdateBody.assetIds es un AssetId[] con marca de tipo
// (core-engine); aquí todo son strings y sólo el PATCH lo necesita.
type BrandedAssetIds = NonNullable<MaintenanceItemUpdateBody["assetIds"]>;

function sameIdSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const setB = new Set(b);
  return a.every((id) => setB.has(id));
}

export type AsignarTiendasDrawerProps = {
  item: MaintenanceItemAdminSummary;
  words: MantencionWords;
  /** Candidatas, ya filtradas a la marca del ítem (todas si no tiene). */
  stores: StorePickerItem[];
  storesLoading: boolean;
  /** El esqueleto de la lista pinta sólo pasado el retardo compartido. */
  storesSkelVisible: boolean;
  /** El ítem tiene marca (brandId no nulo): «Todas» son las de esa marca, no las de la organización. */
  hasBrand: boolean;
  /** Nombre de la marca del ítem, para el copy de «Todas»; null si no se conoce. */
  brandName: string | null;
  onClose: () => void;
  onSaved: (message: string) => void;
};

export function AsignarTiendasDrawer({
  item,
  words,
  stores,
  storesLoading,
  storesSkelVisible,
  hasBrand,
  brandName,
  onClose,
  onSaved,
}: AsignarTiendasDrawerProps) {
  const assetsQuery = useMaintenanceItemAssets(item.id);
  const assetsView = useQueryView(assetsQuery);
  const putAssets = usePutMaintenanceItemAssets();
  const patch = usePatchMaintenanceItem();

  const [scope, setScope] = useState<Scope>(item.scope);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const seededRef = useRef(false);
  const scopeLabelId = useId();

  // La selección parte de lo persistido, una sola vez: un refetch de fondo
  // no pisa una edición en curso.
  useEffect(() => {
    if (seededRef.current || !assetsQuery.data) return;
    seededRef.current = true;
    setSelectedIds(assetsQuery.data.assets.map((a) => a.id));
  }, [assetsQuery.data]);

  const originalIds = useMemo(
    (): string[] => assetsQuery.data?.assets.map((a) => a.id) ?? [],
    [assetsQuery.data]
  );

  const scopeChanged = scope !== item.scope;
  const idsChanged = scope === "assets" && !sameIdSet(selectedIds, originalIds);
  const hasChanges = scopeChanged || idsChanged;
  const isSaving = putAssets.isPending || patch.isPending;
  const assetsReady = assetsView.kind === "ready";
  // Sin tiendas no hay alcance «específicas» que guardar: el ítem
  // desaparecería de todos los paneles (misma guarda que el backend).
  const emptyAssets = scope === "assets" && selectedIds.length === 0;
  const canSave = assetsReady && !storesLoading && !isSaving && hasChanges && !emptyAssets;

  function onScopeChange(next: Scope) {
    // Al pasar de marca a tiendas nada queda marcado: la lista persistida
    // de un ítem de marca es un resto que el gerente nunca confirmó.
    if (next === "assets" && scope === "brand") setSelectedIds([]);
    setScope(next);
  }

  async function save() {
    setError(null);
    try {
      if (!scopeChanged) {
        await putAssets.mutateAsync({ itemId: item.id, assetIds: selectedIds });
      } else if (scope === "assets") {
        await patch.mutateAsync({
          itemId: item.id,
          body: { scope: "assets", assetIds: selectedIds as BrandedAssetIds },
        });
      } else {
        await patch.mutateAsync({ itemId: item.id, body: { scope: "brand" } });
      }
      onSaved(`Asignación guardada · la próxima fecha se recalcula por ${words.assetLower}`);
      onClose();
    } catch (err) {
      const code = err instanceof ApiError ? err.code : null;
      if (code === "MAINTENANCE_ITEM_BRAND_MISMATCH") {
        setError(
          `Una o más ${words.assetPluralLower} ya no pertenecen a la marca de esta mantención. Revisa la selección.`
        );
      } else if (code === "MAINTENANCE_ITEM_SCOPE_EMPTY") {
        setError(`Elige al menos una ${words.assetLower} para «${words.assetPlural} específicas».`);
      } else {
        setError("No pudimos guardar la asignación. Intenta de nuevo.");
      }
    }
  }

  const allLabel = `Todas las ${words.assetPluralLower}`;
  const someLabel = `${words.assetPlural} específicas`;
  // El alcance real de «Todas» (maintenance-scope.ts): la marca del ítem
  // cuando tiene una, aunque su nombre no se haya resuelto todavía.
  const allScopeLabel = hasBrand
    ? brandName
      ? `la marca ${brandName}`
      : "su marca"
    : "la organización";

  return (
    <Drawer
      title={`Asignar ${words.assetPluralLower} · ${item.label}`}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="btn btn--ghost"
            title="Cerrar sin cambiar la asignación"
            disabled={isSaving}
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn--primary"
            title={`Guardar en qué ${words.assetPluralLower} aplica esta mantención`}
            disabled={!canSave}
            onClick={() => void save()}
          >
            {isSaving ? "Guardando…" : "Guardar asignación"}
          </button>
        </>
      }
    >
      {assetsView.kind === "error" ? (
        // Sin la lista persistida no hay diff posible (kept / added /
        // removed): una sección del drawer, el error no mueve el foco.
        <PageError
          title={`No pudimos cargar las ${words.assetPluralLower} de esta mantención.`}
          description="Revisa tu conexión e intenta de nuevo. Si el problema sigue, avísanos."
          onRetry={assetsView.retry}
          autoFocus={false}
        />
      ) : (
        <>
          <div className="field ag-field">
            <span className="field__label" id={scopeLabelId}>
              Aplica a
            </span>
            {/* Bloqueado hasta tener la lista persistida: cambiar el alcance
                antes dejaría que la siembra pisara el vaciado de
                onScopeChange con el resto que nunca debe mostrarse. */}
            <Seg
              labelledBy={scopeLabelId}
              value={scope}
              onChange={onScopeChange}
              disabled={isSaving || !assetsReady}
              options={[
                {
                  value: "brand",
                  label: allLabel,
                  title: `Aplicar a todas las ${words.assetPluralLower} de ${allScopeLabel}`,
                },
                {
                  value: "assets",
                  label: someLabel,
                  title: `Elegir ${words.assetPluralLower} una a una`,
                },
              ]}
            />
          </div>

          {error && (
            <p className="auth-error" role="alert">
              {error}
            </p>
          )}

          {scope === "assets" &&
            (storesLoading || assetsView.kind === "loading" ? (
              <PageSkeleton
                label={`Cargando ${words.assetPluralLower}…`}
                visible={storesSkelVisible || (assetsView.kind === "loading" && assetsView.visible)}
                announce={false}
                kind="section"
              >
                <StorePickerSkel />
              </PageSkeleton>
            ) : (
              <StorePicker
                stores={stores}
                selectedIds={selectedIds}
                onChange={setSelectedIds}
                disabled={isSaving}
                originalIds={originalIds}
                legend={`${words.assetPlural} con esta mantención`}
                addedLabel="Se agrega"
                removedLabel="Se quita"
              />
            ))}

          <p className="field__hint ag-mt8">
            Cada {words.assetLower} lleva su propia fecha de última realización; la próxima se
            calcula desde ahí.
          </p>
        </>
      )}
    </Drawer>
  );
}
